import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MoveRight, Plane } from "lucide-react";
import {
  AIRLINE_DEALS,
  DOMESTIC_ROUTES,
  INTL_ROUTES,
  airlineLogo,
  flightSearchUrl,
  formatINR,
} from "@/mock";

// ---------------- Top Airline Deals ----------------
export const TopAirlineDeals = () => {
  const navigate = useNavigate();
  return (
    <section className="mx-auto max-w-7xl px-4 pt-14">
      <h2 className="text-2xl font-extrabold text-slate-800 md:text-[28px]">
        Top Airline <span className="text-[#1B1F5B]">Deals</span>
      </h2>
      <div className="no-scrollbar -mx-4 mt-6 flex gap-4 overflow-x-auto px-4 pb-3 snap-x">
        {AIRLINE_DEALS.map((d, i) => (
          <button
            key={i}
            data-testid={`airline-deal-${d.from}-${d.to}-${i}`}
            onClick={() => navigate(flightSearchUrl(d.from, d.to))}
            className="group flex w-[300px] shrink-0 snap-start items-center gap-4 rounded-2xl border border-slate-100 bg-white p-4 text-left shadow-[0_5px_20px_rgba(27,31,91,0.07)] transition-all duration-300 hover:-translate-y-1 hover:border-[#F5B32B]/50 hover:shadow-[0_14px_34px_rgba(27,31,91,0.14)]"
          >
            <img
              src={airlineLogo(d.code, 50)}
              alt={d.name}
              className="h-11 w-11 shrink-0 rounded-full border border-slate-100 object-contain p-1"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-sm font-extrabold text-slate-800">
                {d.from}
                <span className="relative flex-1 border-t border-dashed border-slate-300">
                  <Plane className="absolute -top-2 left-1/2 h-3.5 w-3.5 -translate-x-1/2 text-[#F5B32B]" />
                </span>
                {d.to}
              </div>
              <p className="mt-0.5 text-xs text-slate-400">{d.date}</p>
              <p className="mt-1 text-[15px] font-extrabold text-[#1B1F5B]">
                {formatINR(d.price)}
              </p>
            </div>
            <MoveRight className="h-4 w-4 shrink-0 text-slate-300 transition-all duration-200 group-hover:translate-x-1 group-hover:text-[#F5B32B]" />
          </button>
        ))}
      </div>
    </section>
  );
};

// ---------------- Top Routes (Domestic / International) ----------------
export const TopRoutes = () => {
  const [tab, setTab] = useState("domestic");
  const navigate = useNavigate();
  const list = tab === "domestic" ? DOMESTIC_ROUTES : INTL_ROUTES;

  return (
    <section className="mx-auto max-w-7xl px-4 pt-14">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-extrabold text-slate-800 md:text-[28px]">
          Top {tab === "domestic" ? "Domestic" : "International"}{" "}
          <span className="text-[#1B1F5B]">Routes</span>
        </h2>
        <div className="flex rounded-full border border-slate-200 bg-white p-1 shadow-sm">
          {["domestic", "international"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-full px-5 py-1.5 text-sm font-semibold capitalize transition-all duration-200 ${
                tab === t
                  ? "bg-[#1B1F5B] text-white shadow"
                  : "text-slate-500 hover:text-[#1B1F5B]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {list.map((r, i) => (
          <button
            key={i}
            data-testid={`top-route-${r.from}-${r.to}-${i}`}
            onClick={() => navigate(flightSearchUrl(r.from, r.to))}
            className="group rounded-2xl border border-slate-100 bg-white p-4 text-left shadow-[0_5px_20px_rgba(27,31,91,0.06)] transition-all duration-300 hover:-translate-y-1 hover:border-[#1B1F5B]/30 hover:shadow-[0_14px_34px_rgba(27,31,91,0.13)]"
          >
            <div className="flex items-center justify-between">
              <img
                src={airlineLogo(r.code, 90)}
                alt={r.name}
                loading="lazy"
                className="h-9 w-9 rounded-full border border-slate-100 object-contain p-0.5"
              />
              <span className="rounded-full bg-[#FFF9EC] px-2.5 py-1 text-[11px] font-bold text-[#B57A00]">
                Economy
              </span>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <span className="text-lg font-extrabold text-slate-800">{r.from}</span>
              <span className="relative flex-1 border-t-2 border-dashed border-slate-200">
                <Plane className="absolute -top-2.5 left-1/2 h-4 w-4 -translate-x-1/2 rotate-0 text-[#1B1F5B] transition-transform duration-300 group-hover:translate-x-2" />
              </span>
              <span className="text-lg font-extrabold text-slate-800">{r.to}</span>
            </div>
            <div className="mt-2.5 flex items-center justify-between">
              <span className="text-xs text-slate-400">{r.name}</span>
              <span className="text-[15px] font-extrabold text-[#1B1F5B]">
                {formatINR(r.price)}
              </span>
            </div>
            <div className="mt-3 flex items-center justify-center gap-1.5 rounded-lg bg-[#1B1F5B] py-2 text-xs font-extrabold uppercase tracking-wide text-white transition-colors group-hover:bg-[#F5B32B] group-hover:text-[#0A0F2E]">
              <Plane className="h-3.5 w-3.5" /> Search Flight
            </div>
          </button>
        ))}
      </div>
    </section>
  );
};
